/*
 * File: ChargeSchema.ts
 * Author: Austin Robinson
 * Date Created: 9/28/2024
 * Copyright (c) DrivingDoc 2024
 */
import { z } from 'zod';

export const DiscountSchema = z.object({
  id: z.string().uuid().nullable().readonly(),
  discount_id: z.string({ required_error: 'Discount is required' }).uuid(),
  order: z.number().positive().readonly(),
  type: z.enum(['fixed', 'percentage']),
  amount: z.number().nonnegative(),
});

export const BaseChargeFormSchema = z.object({
  // id: z.string().uuid().readonly(),
  id: z.string().uuid().nullable().readonly(),
  student_user_id: z.string().uuid().readonly(),
  memo: z
    .string({
      required_error: 'Memo is required',
    })
    .min(1),
  description: z.string().nullable().default(null),
  private_notes: z.string().nullable().default(null),
  amount: z
    .number({
      required_error: 'Amount is required',
      invalid_type_error: 'Expected a number',
    })
    .positive(),
  amount_due: z
    .string()
    .datetime()
    .nullable()
    .default(() => new Date().toISOString()),
  amount_due_lock: z.string().datetime().nullable().default(null),
  deposit: z
    .number({
      required_error: 'Amount is required',
      invalid_type_error: 'Expected a number',
    })
    .nonnegative()
    .nullable()
    .default(0),
  deposit_due: z.string().datetime().nullable().default(null),
  deposit_due_lock: z.string().datetime().nullable().default(null),
  student_charge_discounts: z.array(DiscountSchema).default([]),
});

export const ChargeFormSchema = BaseChargeFormSchema.refine((data) => (data.deposit ?? 0) <= data.amount, {
  message: 'Deposit cannot be greater than total amount',
  path: ['deposit'],
})
  .refine((data) => !data.deposit_due || data.deposit_due <= (data.amount_due ?? 0), {
    message: 'Deposit due date cannot be after total amount due date',
    path: ['deposit_due'],
  })
  .refine((data) => !data.amount_due_lock || data.amount_due_lock >= (data.amount_due ?? 0), {
    message: 'Account lock date must be on or after due date',
    path: ['amount_due_lock'],
  })
  .refine((data) => !data.deposit_due_lock || data.deposit_due_lock >= (data.deposit_due ?? 0), {
    message: 'Account lock date must be on or after due datee',
    path: ['deposit_due_lock'],
  });

export type DiscountFieldValue = z.infer<typeof DiscountSchema>;
export type ChargeFormValues = z.infer<typeof ChargeFormSchema>;
